import { type Prettify } from '@craft/lib';

import { type Dimension, Dimensions, type SingleDimension } from '../types/dimensions';
import { type QuantitativeSize } from '../types/sizes';

export const isDimension = (value: unknown): value is Dimension =>
  Dimensions.includes(value as Dimension);

/**
 * Parses the {@link SingleDimension<T>}, returning the dimension that the size is specified for
 * and the size value, {@link T}, itself.
 *
 * @param {SingleDimension<T>} size The size specification along a single dimension.
 *
 * @returns {[T, SizeDimension]} The dimension and size value {@link T}.
 */
export const parseSingleDimension = <T extends QuantitativeSize>(
  size: SingleDimension<T>,
): [T, Dimension] => {
  if ('height' in size && size.height !== undefined) {
    return [size.height, 'height'];
  } else if ('width' in size && size.width !== undefined) {
    return [size.width, 'width'];
  }
  throw new Error("The provided size does not have a valid 'height' or 'width' property!");
};

const OppositeDimensions = {
  height: 'width',
  width: 'height',
} as const satisfies { [key in Dimension]: Exclude<Dimension, key> };

export type OppositeDimension<D extends Dimension> = (typeof OppositeDimensions)[D];

export const getOppositeDimension = <D extends Dimension>(dimension: D): OppositeDimension<D> =>
  OppositeDimensions[dimension];

/**
 * A typeguard that determines whether or not the provided value is a {@link SingleDimension}
 * expression of the size {@link T} or simply the size {@link T} alone.
 *
 * This typeguard should not be exported or used outside of this file, and is only applicable/safe
 * to use for purposes of the {@link getFullDimensions} function below.
 */
const isSingleDimension = <T extends QuantitativeSize>(
  size: SingleDimension<T> | T,
): size is SingleDimension<T> => typeof size === 'object';

export type OppositeDimensionSizing = 'auto' | 'square' | QuantitativeSize;

type OppositeDimensionSize<
  D extends QuantitativeSize,
  O extends OppositeDimensionSizing,
> = O extends 'auto' ? 'auto' : O extends 'square' ? D : O extends QuantitativeSize ? O : never;

const oppositeDimensionSizing = <D extends QuantitativeSize, O extends OppositeDimensionSizing>(
  primarySize: D,
  oppositeTreatment: O,
): OppositeDimensionSize<D, O> =>
  (oppositeTreatment === 'auto'
    ? 'auto'
    : oppositeTreatment === 'square'
      ? primarySize
      : oppositeTreatment) as OppositeDimensionSize<D, O>;

type FullSingleDimension<
  S extends SingleDimension,
  O extends OppositeDimensionSizing = 'auto',
> = S extends {
  height: infer H extends QuantitativeSize;
}
  ? { height: H; width: OppositeDimensionSize<H, O> }
  : S extends { width: infer W extends QuantitativeSize }
    ? { height: OppositeDimensionSize<W, O>; width: W }
    : never;

type FullDimensionSize<
  S extends QuantitativeSize,
  A extends Dimension,
  O extends OppositeDimensionSizing = 'auto',
> = Prettify<Record<A, S> & Record<OppositeDimension<A>, OppositeDimensionSize<S, O>>>;

export function getFullDimensions<
  S extends SingleDimension,
  O extends OppositeDimensionSizing = 'auto',
>(size: S, oppositeDimension?: O): FullSingleDimension<S, O>;

export function getFullDimensions<
  S extends QuantitativeSize,
  A extends Dimension,
  O extends OppositeDimensionSizing = 'auto',
>(size: S, primaryDimension: A, oppositeTreatment?: O): FullDimensionSize<S, A, O>;

/**
 * Given a size value, {@link S}, a dimension in which to size with that value, {@link A}, and
 * a specification for how to treat the opposite dimension, {@link O}, this function will return an
 * object that contains the full dimensions (i.e. both "height" and "width") which is constructed
 * by sizing the primary dimension and treating the opposite dimension as specified.
 *
 * This method can be called in multiple ways for purposes of flexibility and ease of use.
 *
 * @example
 * // Returns { width: "30px", height: "30px" }
 * getFullDimensions({ height: "30px" }, 'square')
 * @example
 * // Returns { height: "30px", width: "30px" }
 * getFullDimensions("30px", "width", 'square')
 */
export function getFullDimensions<
  S extends QuantitativeSize,
  A extends Dimension,
  O extends OppositeDimensionSizing = 'auto',
>(size: S, arg0?: A | O, arg1?: O) {
  if (isSingleDimension(size)) {
    const [value, dimension] = parseSingleDimension(size);
    if (arg0 && isDimension(arg0)) {
      throw new Error(
        'Invalid method implementation! Expected the second argument to be of type ' +
          "'OppositeDimensionSizing'.",
      );
    }
    const oppositeDimensionTreatment = arg0 ?? ('auto' as O);
    return {
      [dimension]: value,
      [getOppositeDimension(dimension)]: oppositeDimensionSizing(value, oppositeDimensionTreatment),
    };
  }
  if (!arg0 || !isDimension(arg0)) {
    throw new Error(
      "Invalid method implementation! Expected the second argument to be either 'height' or " +
        "'width'.",
    );
  }
  return {
    [arg0]: size,
    [getOppositeDimension(arg0)]: oppositeDimensionSizing(size, arg1 ?? 'auto'),
  };
}
